import type { PluginRegistry } from "plugins/plugin-registry"

import { AddOns, SidePanelLayerType } from "@nova/constants"
import { ColorfinderPluginActionBody } from "../plugin-actions/ColorfinderPluginActionBody"
import { ModalNames } from "features/modals/types"
import { ImageColorFinderFlight, VideoColorFinderFlight } from "@nova/protobuf"
import {
    selectColorFinderFlightById,
    selectImageColorFinderFlightById,
    selectVideoColorFinderFlightById,
    selectVideoColorFinderFlights,
} from "features/entity-caching/flights/selectors"
import { selectAllImageColorfinderFlights } from "features/entity-caching/images/colorfinder/selectors"
import { InspectorPanelICFData } from "features/map/inspector-panel/color-finder/ICF/InspectorPanelICFData"
import { InspectorPanelICFLayer } from "features/map/inspector-panel/color-finder/ICF/InspectorPanelICFLayer"
import { InspectorPanelVCFData } from "features/map/inspector-panel/color-finder/VCF/InspectorPanelVCFData"
import { InspectorPanelVCFLayer } from "features/map/inspector-panel/color-finder/VCF/InspectorPanelVCFLayer"
import { getGenericLayerDataInspectorPanelComponent } from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import {
    deleteImageColorFinderFlight,
    updateImageColorFinderFlight,
} from "features/pubsub/entityActions/flights/imageColorFinder"
import {
    deleteVideoColorFinderFlight,
    updateVideoColorFinderFlight,
} from "features/pubsub/entityActions/flights/videoColorFinder"
import {
    getGenericActiveFeatureSidePanelComponent,
    getGenericMultistepUploadFlowActiveFeatureSidePanelComponent,
    getGenericUploadFlowActiveFeatureSidePanelComponent,
} from "panels/content/side-panel/active-feature-components/GenericActiveFeatureSidePanelComponent"
import { getGenericLayerListItem } from "panels/content/side-panel/generics/GenericLayerListItem"

import { ColorFinderMultifileUpload } from "./ColorFinderMultifileUpload"
import { getUseColorfinderListItemActions } from "panels/content/side-panel/generics/genericListItemActions"
import {
    useImageColorfinderStatusAndProgress,
    useVideoColorfinderStatusAndProgress,
} from "panels/content/side-panel/progress/Colorfinder"
import { getGenericAssetsListItem } from "panels/content/side-panel/generics/genericAssetsListItems"
import { ImageColorfinderAssetsThumbnail } from "panels/content/side-panel/assets-thumbnails/ImageColorfinderAssetsThumbnail"
import { VideoColorfinderAssetsThumbnail } from "panels/content/side-panel/assets-thumbnails/VideoColorfinderAssetsThumbnail"
import { useTopBarRegistry } from "panels/routing/TopBarContext"
import { useLayoutEffect } from "react"
import { ColorFinderTopBar } from "../V2/ColorFinderTopBar"

import { selectColorFinderFlightId, selectColorFinderIsOpen } from "../V2/redux/selectors"
import { zoomToColorFinder } from "features/map/inspector-panel/color-finder/module"
import { useCFFrameBBOXLayer } from "./layers/frameBbox"
import { useCFDroneIconLayer } from "./layers/droneIcon"

import { useAppSelector } from "integrations/redux/hooks"
import * as Icons from "@nova/component-icons"
import { getUseColorfinderMapNodes } from "features/map/map-nodes/useColorfinderMapNodes"

export function registerColorFinderPlugin(registry: PluginRegistry) {
    registry
        .register<ImageColorFinderFlight>({
            name: "imageColorfinder",
            displayName: "Colorfinder",
            sidePanelLayerType: SidePanelLayerType.ImageColorFinder,
            PrimaryIcon: Icons.ImageColorfinderPlugin,
            ImageIconUrl: "/images/plugins/layers/colorfinder.svg",
            updateAction: updateImageColorFinderFlight,
            deleteAction: deleteImageColorFinderFlight,
            confirmDelete: () => true,
            protobufMessage: ImageColorFinderFlight,
            selectAllEntities: selectAllImageColorfinderFlights,
            selectSingleEntity: selectImageColorFinderFlightById,
            useUploadStatusAndProgress: useImageColorfinderStatusAndProgress,
            zoomTo: zoomToColorFinder,
            duplicate: () => undefined,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin", (spec) => ({
            SidePanelListItem: getGenericLayerListItem({
                spec,
                useActions: getUseColorfinderListItemActions(spec),
            }),
        }))
        .implement("side-panel-assets-plugin", (spec) => ({
            categoryId: "colorfinder",
            SidePanelListItem: getGenericAssetsListItem({
                spec,
                ThumbnailRender: ImageColorfinderAssetsThumbnail,
            }),
        }))
        .implement(
            "side-panel-active-feature-plugin",
            (spec) =>
                ({
                    selectSingleEntity: selectColorFinderFlightById,
                    SidePanelComponent:
                        getGenericMultistepUploadFlowActiveFeatureSidePanelComponent({
                            InitialRenderComponent: getGenericActiveFeatureSidePanelComponent({
                                spec,
                                RenderComponent: ColorfinderPluginActionBody,
                            }),
                            UploadFlowRenderComponent:
                                getGenericUploadFlowActiveFeatureSidePanelComponent({
                                    MultifileUploadComponent: ColorFinderMultifileUpload,
                                }),
                        }),
                }) as const
        )
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getGenericLayerDataInspectorPanelComponent({
                spec,
                LayerComponent: InspectorPanelICFLayer,
                DataComponent: InspectorPanelICFData,
            }),
            useSubtitles,
        }))
        .implement("map-node-plugin", (spec) => ({
            useMapNodes: getUseColorfinderMapNodes(spec),
        }))

    registry
        .register<VideoColorFinderFlight>({
            name: "videoColorfinder",
            displayName: "Colorfinder",
            sidePanelLayerType: SidePanelLayerType.VideoColorFinder,
            PrimaryIcon: Icons.ImageColorfinderPlugin,
            ImageIconUrl: "/images/plugins/layers/colorfinder.svg",
            updateAction: updateVideoColorFinderFlight,
            deleteAction: deleteVideoColorFinderFlight,
            confirmDelete: () => true,
            protobufMessage: VideoColorFinderFlight,
            selectAllEntities: selectVideoColorFinderFlights,
            selectSingleEntity: selectVideoColorFinderFlightById,
            useUploadStatusAndProgress: useVideoColorfinderStatusAndProgress,
            zoomTo: zoomToColorFinder,
            duplicate: () => undefined,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin", (spec) => ({
            SidePanelListItem: getGenericLayerListItem({
                spec,
                useActions: getUseColorfinderListItemActions(spec),
            }),
        }))
        .implement("side-panel-assets-plugin", (spec) => ({
            categoryId: "colorfinder",
            useThisCategoryHeader: false,
            SidePanelListItem: getGenericAssetsListItem({
                spec,
                ThumbnailRender: VideoColorfinderAssetsThumbnail,
            }),
        }))
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getGenericLayerDataInspectorPanelComponent({
                spec,
                LayerComponent: InspectorPanelVCFLayer,
                DataComponent: InspectorPanelVCFData,
            }),
            useSubtitles,
        }))
        // doesn't matter which plugin these are applied to, only matters that they are applied once and not twice
        .implement("map-layer-plugin", () => ({
            useLayer: useCFDroneIconLayer,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useCFFrameBBOXLayer,
        }))
        .implement("ui-controller", () => ({
            Controller: () => {
                const isColorFinderOpen = useAppSelector(selectColorFinderIsOpen)
                const flightId = useAppSelector(selectColorFinderFlightId)
                const { register, unregister } = useTopBarRegistry()

                useLayoutEffect(() => {
                    if (isColorFinderOpen && flightId) {
                        register({ Item: ColorFinderTopBar, key: "color-finder-top-bar" })
                    } else {
                        unregister("color-finder-top-bar")
                    }
                }, [flightId, isColorFinderOpen, register, unregister])

                return <div></div>
            },
        }))
        .implement("feature-plugin", () => ({
            subscriptionType: AddOns.Colorfinder,
            sampleDataModalName: ModalNames.TryColorfinderModal,
        }))
        .implement("map-node-plugin", (spec) => ({
            useMapNodes: getUseColorfinderMapNodes(spec),
        }))
}

function useSubtitles(id: string) {
    const flight = useAppSelector(selectImageColorFinderFlightById(id))
    if (!flight?.images?.length) return []
    return [`${flight.images.length} images`]
}
